/**
 * Shared domain types for the SBD TCIC Replacement Service Center Platform.
 * Consumed by both apps/api (Workers) and apps/web (Pages SPA) so request/response
 * shapes can never silently drift between frontend and backend.
 *
 * Traceability: 02_Requirements_And_Scope.md (FR-*, personas), 04_Target_Solution_Architecture.md (DATA-*).
 */

// ---------------------------------------------------------------------------
// RBAC — 02 User Personas table (23 personas), 04 ADR-013
// ---------------------------------------------------------------------------

export const ROLES = [
  "COUNTER_REP",
  "TECHNICIAN",
  "SERVICE_CENTER_MANAGER",
  "INVENTORY_SPECIALIST",
  "RECEIVING_SPECIALIST",
  "SHIPPING_SPECIALIST",
  "OPERATIONS_MANAGER",
  "OPERATIONS_TEAM",
  "MARKETING",
  "AUTHORIZED_NETWORK_MANAGER",
  "IT_SPECIALIST",
  "PRODUCT_OWNER",
  "REGIONAL_MANAGER",
  "DIRECTOR",
  "ECOMMERCE_MANAGER",
  "OVERHEAD_ASSOCIATE",
  "PRO_CENTER_REP",
  // Phase 2 personas (FR-FSC-*, FR-TSQ-*) — role exists for future use, no Phase-1 screens grant them extra scope.
  "FIELD_SERVICE_COORDINATOR",
  "TECHNICAL_SERVICE_TEAM",
] as const;
export type Role = (typeof ROLES)[number];

/** Coarse-grained permission keys checked by the shared RBAC middleware (05 STD-002). */
export const PERMISSIONS = [
  "pos.create_order",
  "pos.void_transaction",
  "repair_orders.create",
  "repair_orders.work",
  "repair_orders.complete_payment",
  "inventory.read",
  "inventory.write",
  "inventory.approve_scrap",
  "inventory.approve_eo_pricing",
  "receiving.receive",
  "shipping.manage",
  "order_history.read",
  "order_history.notify",
  "service_programs.admin",
  "service_programs.read",
  "management_oversight.approve_pricing",
  "management_oversight.approve_spr",
  "management_oversight.review_void_fraud",
  "dashboard.read",
  "reports.read",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/** Default permission grants per role — the seed for the `role_permissions` table (DATA-018). */
export const DEFAULT_ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  COUNTER_REP: [
    "pos.create_order",
    "repair_orders.create",
    "inventory.read",
    "order_history.read",
    "order_history.notify",
    "service_programs.read",
    "dashboard.read",
  ],
  TECHNICIAN: [
    "repair_orders.create",
    "repair_orders.work",
    "repair_orders.complete_payment",
    "inventory.read",
    "dashboard.read",
  ],
  SERVICE_CENTER_MANAGER: [
    "pos.create_order",
    "pos.void_transaction",
    "repair_orders.create",
    "repair_orders.work",
    "repair_orders.complete_payment",
    "inventory.read",
    "inventory.write",
    "inventory.approve_scrap",
    "inventory.approve_eo_pricing",
    "receiving.receive",
    "shipping.manage",
    "order_history.read",
    "order_history.notify",
    "service_programs.admin",
    "service_programs.read",
    "management_oversight.approve_pricing",
    "management_oversight.approve_spr",
    "management_oversight.review_void_fraud",
    "dashboard.read",
    "reports.read",
  ],
  INVENTORY_SPECIALIST: ["inventory.read", "inventory.write", "receiving.receive", "dashboard.read"],
  RECEIVING_SPECIALIST: ["receiving.receive", "inventory.read", "dashboard.read"],
  SHIPPING_SPECIALIST: ["shipping.manage", "order_history.read", "dashboard.read"],
  OPERATIONS_MANAGER: [
    "management_oversight.approve_pricing",
    "management_oversight.approve_spr",
    "management_oversight.review_void_fraud",
    "order_history.read",
    "dashboard.read",
    "reports.read",
  ],
  OPERATIONS_TEAM: ["management_oversight.approve_pricing", "management_oversight.approve_spr", "reports.read"],
  MARKETING: ["service_programs.admin", "service_programs.read", "management_oversight.approve_spr"],
  AUTHORIZED_NETWORK_MANAGER: ["service_programs.read", "reports.read"],
  IT_SPECIALIST: ["dashboard.read", "reports.read"],
  PRODUCT_OWNER: ["dashboard.read", "reports.read"],
  REGIONAL_MANAGER: ["dashboard.read", "reports.read", "management_oversight.review_void_fraud"],
  DIRECTOR: ["dashboard.read", "reports.read", "management_oversight.review_void_fraud"],
  ECOMMERCE_MANAGER: ["service_programs.read", "order_history.read"],
  OVERHEAD_ASSOCIATE: ["dashboard.read"],
  PRO_CENTER_REP: ["pos.create_order", "repair_orders.create", "inventory.read", "order_history.read"],
  FIELD_SERVICE_COORDINATOR: ["order_history.read"],
  TECHNICAL_SERVICE_TEAM: ["inventory.read"],
};

export interface SessionUser {
  id: string;
  name: string;
  role: Role;
  serviceCenterId: string;
  permissions: Permission[];
}

// ---------------------------------------------------------------------------
// Core domain entities (04 DATA-001…020)
// ---------------------------------------------------------------------------

export type CustomerType = "B2B" | "B2C";
export type AccountStatus = "On Account" | "Cash Customer";

export interface Customer {
  id: string;
  customerNumber: string;
  name: string;
  type: CustomerType;
  status: AccountStatus;
  address: string;
  phone: string;
  email: string;
  contact: string;
  vipFlag: boolean;
  notificationOptInEmail: boolean;
  notificationOptInSms: boolean;
}

export type PartStatus = "In Stock" | "Low Stock" | "Out of Stock";

export interface Part {
  partNumber: string;
  description: string;
  brand: string;
  category: string;
  manufacturer: string;
  bin: string;
  onHand: number;
  available: number;
  onOrder: number;
  allocated: number;
  minQty: number;
  safetyStock: number;
  uom: string;
  listPrice: number;
  yourPrice: number;
  stdCost: number;
  status: PartStatus;
}

export type OrderType = "Sales Order" | "Repair Order" | "Return Order" | "Warranty Exchange" | "Satisfaction Refund" | "Quote";
export type OrderStatus = "Open" | "Closed";
export type FulfillmentStatus = "N/A" | "Ready to Ship" | "In Picking" | "Shipped";
export type NotificationStatus = "Sent" | "Not Sent" | "Pending Approval" | "Backorder Parts" | "Shipped";

export interface LineItem {
  partNumber: string;
  description: string;
  qty: number;
  listPrice: number;
  yourPrice: number;
  deliveryMethod: "Carry Out" | "Ship Out" | "Ship Direct";
  extPrice: number;
}

export interface Order {
  orderNumber: string;
  orderType: OrderType;
  orderMethod: string;
  serviceType: string;
  orderDate: string;
  customerId: string;
  customerName: string;
  value: number;
  status: OrderStatus;
  fulfillmentStatus: FulfillmentStatus;
  paymentMethod: string;
  notificationStatus: NotificationStatus;
  createdBy: string;
  reason: string;
  lineItems: LineItem[];
}

export type RepairStatus = "Open" | "In Progress" | "Complete";
export type DeliveryMethod = "Ship Out to Customer" | "Customer Pickup";
export type SafetyCheckState = "Pass" | "Fail" | "Pending";

export interface SafetyChecks {
  leakage: SafetyCheckState;
  ground: SafetyCheckState;
  hipot1000: SafetyCheckState;
  hipot2500: SafetyCheckState;
  tested: SafetyCheckState;
  cleaned: SafetyCheckState;
  installedSafety: SafetyCheckState;
}

export const SAFETY_CHECK_KEYS = ["leakage", "ground", "hipot1000", "hipot2500", "tested", "cleaned", "installedSafety"] as const;

export interface PartUsed {
  id: string;
  part: string;
  desc: string;
  qty: number;
  stdCost: number;
  listPrice: number;
  /** Real fields joined from the parts catalog (bin location, available/ATP stock, category —
   *  category drives the same representative icon used in POS). Null only if the part record was
   *  since removed from the catalog. */
  bin: string | null;
  available: number | null;
  category: string | null;
}

export interface RepairOrder {
  id: string;
  customerId: string;
  customerName: string;
  shipTo: string;
  equipment: string;
  modelNumber: string;
  dateCode: string;
  serialNumber: string;
  typeNumber: string;
  serviceType: string;
  grcPrice: number;
  grcIsPlaceholder: boolean;
  toolFailure: string;
  priority: "FIFO" | "Expedited" | "VIP";
  manufacturingStatus: string;
  gsisRecommendation: string;
  status: RepairStatus;
  promisedDate: string;
  createdDate: string;
  deliveryMethod: DeliveryMethod;
  freightMethod: string;
  freightRate: number;
  transit: string;
  technician: string | null;
  laborRate: number;
  laborHours: number;
  laborStart: string | null;
  problemDescription: string;
  resolutionCode: string;
  techNotes: string;
  publicNotes: string;
  partsUsed: PartUsed[];
  safetyChecks: SafetyChecks;
  inspectedBy: string;
  inspectionDate: string | null;
}

export interface Carton {
  id: string;
  vendor: string;
  poNumber: string;
  receivedDate: string;
  status: "Not Started" | "In Progress" | "Received";
  lines: CartonLine[];
}

export interface CartonLine {
  partNumber: string;
  description: string;
  qty: number;
  uom: string;
  bin: string | null;
}

export interface Shipment {
  id: string;
  orderNumber: string;
  trackingNumber: string | null;
  carrier: string | null;
  method: string;
  shippedDate: string | null;
}

export interface NotificationRecord {
  id: string;
  customerId: string;
  channel: "email" | "sms" | "print";
  content: string;
  status: "pending" | "sent" | "failed" | "dead_letter";
  retryCount: number;
  createdAt: string;
  sentAt: string | null;
}

export interface ScrapRequest {
  id: string;
  partNumber: string;
  reason: string;
  requestedBy: string;
  status: "Pending" | "Approved" | "Rejected";
  approvedBy: string | null;
}

export interface SprRequest {
  id: string;
  requestedBy: string;
  customerId: string | null;
  discount: string;
  status: "Pending" | "Approved" | "Rejected";
  approvedBy: string | null;
  expiration: string | null;
}

export interface VoidTransactionRecord {
  id: string;
  transactionRef: string;
  reasonCode: string;
  approvedBy: string;
  createdAt: string;
}

export interface FraudFlag {
  id: string;
  transactionRef: string;
  ruleMatched: string;
  status: "Open" | "Reviewed" | "Confirmed" | "Dismissed";
  createdAt: string;
}

export interface ServiceProgram {
  id: string;
  name: string;
  icon: string;
  internalDescription: string;
  externalDescription: string;
  details: string;
  category: string;
}

export interface GrcPricingRow {
  modelNumber: string;
  category: string;
  price: number;
  isPlaceholder: boolean;
}

export interface ReportRun {
  id: string;
  runDate: string;
  reportType: string;
  status: "success" | "failed";
}

export interface AuditLogEntry {
  id: string;
  actorId: string;
  actionType: string;
  entityRef: string;
  timestamp: string;
  detail: string;
}

// ---------------------------------------------------------------------------
// API envelope
// ---------------------------------------------------------------------------

export interface ApiError {
  error: string;
  message: string;
  details?: Record<string, unknown>;
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: ApiError };

export function ok<T>(data: T): ApiResult<T> {
  return { ok: true, data };
}
export function fail(error: string, message: string, details?: Record<string, unknown>): ApiResult<never> {
  return { ok: false, error: { error, message, details } };
}
